import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, Clock, User, Sparkles,
  Newspaper, Mail, FileText
} from 'lucide-react';
import landingService from '../../../services/landingService';
import ResourcesLayout from './ResourcesLayout';
import './BlogPage.css';

const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [posts, setPosts] = useState([]);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState({ message: '', type: '' });
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionRefs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.id]));
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -60px 0px' }
    );

    Object.values(sectionRefs.current).forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, [loading, featuredPost]);

  // Fetch blog posts from API
  useEffect(() => {
    const fetchPosts = async () => {
      setLoading(true);
      const params = { page: 1, limit: 50 };
      if (activeCategory !== 'all') params.category = activeCategory;
      const result = await landingService.getBlogPosts(params);
      if (result.success) {
        const allPosts = result.posts || [];
        const featured = allPosts.find((p) => p.isFeatured);
        const rest = allPosts.filter((p) => !p.isFeatured);
        setFeaturedPost(featured || null);
        setPosts(rest);
      }
      setLoading(false);
    };
    fetchPosts();
  }, [activeCategory]);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    setNewsletterLoading(true);
    setNewsletterStatus({ message: '', type: '' });
    const result = await landingService.subscribeNewsletter(newsletterEmail, 'blog_page');
    setNewsletterStatus({
      message: result.message,
      type: result.success ? 'success' : 'error',
    });
    if (result.success) setNewsletterEmail('');
    setNewsletterLoading(false);
  };

  const isVisible = (id) => visibleSections.has(id);

  const categories = [
    { id: 'all', label: 'All Posts' },
    { id: 'product', label: 'Product' },
    { id: 'research', label: 'Research' },
    { id: 'community', label: 'Community' },
    { id: 'engineering', label: 'Engineering' },
  ];

  const getCategoryLabel = (cat) => {
    const map = { product: 'Product', research: 'Research', community: 'Community', engineering: 'Engineering' };
    return map[cat] || cat;
  };

  const getCategoryColor = (cat) => {
    const map = { product: 'green', research: 'blue', community: 'purple', engineering: 'amber' };
    return map[cat] || 'green';
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <ResourcesLayout>
      {/* ── Hero ── */}
      <section className="blog-hero">
        <div className="blog-hero-inner">
          <h1 className="blog-hero-title">
            Latest updates & <span className="blog-hero-gradient">insights</span>
          </h1>
          <p className="blog-hero-subtitle">
            Product updates, research best practices, engineering deep dives, 
            and stories from the ResearchPro community.
          </p>
        </div>
      </section>

      {/* ── Featured Post ── */}
      {featuredPost && (
        <section
          id="blog-featured"
          className={`blog-section ${isVisible('blog-featured') ? 'blog-visible' : ''}`}
          ref={(el) => (sectionRefs.current['blog-featured'] = el)}
        >
          <div className="blog-section-inner">
            <Link to={`/blog/${featuredPost.slug}`} className="blog-featured-card">
              <div className="blog-featured-visual">
                <Sparkles size={40} className="blog-featured-sparkle" />
              </div>
              <div className="blog-featured-content">
                <div className="blog-featured-meta">
                  <span className="blog-tag blog-tag--green">Featured</span>
                  <span className="blog-meta-cat">{getCategoryLabel(featuredPost.category)}</span>
                  <span className="blog-meta-sep">·</span>
                  <span className="blog-meta-date">{formatDate(featuredPost.publishedAt)}</span>
                </div>
                <h2 className="blog-featured-title">{featuredPost.title}</h2>
                <p className="blog-featured-desc">{featuredPost.description}</p>
                <div className="blog-featured-footer">
                  <div className="blog-author">
                    <div className="blog-author-avatar">
                      {featuredPost.author?.name?.split(' ').map((n) => n[0]).join('') || 'RP'}
                    </div>
                    <div>
                      <span className="blog-author-name">{featuredPost.author?.name}</span>
                      {featuredPost.author?.role && (
                        <span className="blog-author-role">{featuredPost.author.role}</span>
                      )}
                    </div>
                  </div>
                  <span className="blog-read-time">
                    <Clock size={13} /> {featuredPost.readTime}
                  </span>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* ── All Posts ── */}
      <section
        id="blog-posts"
        className={`blog-section blog-section--alt ${isVisible('blog-posts') ? 'blog-visible' : ''}`}
        ref={(el) => (sectionRefs.current['blog-posts'] = el)}
      >
        <div className="blog-section-inner">
          <div className="blog-section-header">
            <h2 className="blog-section-title">All Posts</h2>
            <p className="blog-section-subtitle">Browse by category or read the latest</p>
          </div>

          <div className="blog-tabs">
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`blog-tab ${activeCategory === cat.id ? 'blog-tab--active' : ''}`}
                onClick={() => setActiveCategory(cat.id)}
              >
                {cat.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="blog-empty">
              <Newspaper size={36} />
              <p>Loading posts...</p>
            </div>
          ) : (
            <div className="blog-posts-grid">
              {posts.map((post, i) => (
                <Link key={post._id || i} to={`/blog/${post.slug}`} className="blog-post-card">
                  <div className={`blog-post-icon blog-post-icon--${getCategoryColor(post.category)}`}>
                    <FileText size={18} />
                  </div>
                  <div className="blog-post-body">
                    <div className="blog-post-meta">
                      <span className={`blog-cat blog-cat--${getCategoryColor(post.category)}`}>
                        {getCategoryLabel(post.category)}
                      </span>
                      <span className="blog-meta-sep">·</span>
                      <span className="blog-meta-date">{formatDate(post.publishedAt)}</span>
                    </div>
                    <h3 className="blog-post-title">{post.title}</h3>
                    <p className="blog-post-desc">{post.description}</p>
                    <div className="blog-post-footer">
                      <span className="blog-post-author">
                        <User size={12} /> {post.author?.name}
                      </span>
                      <span className="blog-read-time">
                        <Clock size={12} /> {post.readTime}
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {!loading && posts.length === 0 && (
            <div className="blog-empty">
              <Newspaper size={36} />
              <p>No posts in this category yet.</p>
            </div>
          )}
        </div>
      </section>

      {/* ── Newsletter ── */}
      <section className="blog-newsletter">
        <div className="blog-newsletter-inner">
          <div className="blog-newsletter-content">
            <span className="blog-newsletter-badge">
              <Mail size={14} />
              Newsletter
            </span>
            <h2 className="blog-newsletter-title">Stay in the loop</h2>
            <p className="blog-newsletter-subtitle">
              Product updates, research tips, and community highlights — delivered weekly.
            </p>
          </div>
          <form className="blog-newsletter-form" onSubmit={handleNewsletterSubmit}>
            <input
              type="email"
              placeholder="you@university.edu"
              className="blog-newsletter-input"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              required
            />
            <button className="blog-newsletter-btn" type="submit" disabled={newsletterLoading}>
              {newsletterLoading ? 'Subscribing...' : 'Subscribe'}
              {!newsletterLoading && <ArrowRight size={15} />}
            </button>
          </form>
          {newsletterStatus.message ? (
            <p className={`blog-newsletter-note ${newsletterStatus.type === 'error' ? 'blog-newsletter-error' : 'blog-newsletter-success'}`}>
              {newsletterStatus.message}
            </p>
          ) : (
            <p className="blog-newsletter-note">No spam. Unsubscribe anytime.</p>
          )}
        </div>
      </section>
    </ResourcesLayout>
  );
};

export default BlogPage;
